"use strict";
/**
 * Order Form Events v4 — marketing/campaign param capture (client side).
 *
 * The allow-list is NOT defined here. The server injects it as `window.W3_TRACKED_PARAMS` from
 * src/config/trackedParams.ts — the single source of truth — so adding or removing a param is a
 * server deploy and never a webpack rebuild. FALLBACK_CONFIG is used only when the injection is
 * missing (an old cached page, or a bundle loaded outside the order-form views).
 *
 * Matching is by PREFIX (`w3_`, `utm_`) plus a handful of exact keys, so every param HQ's tagging
 * module mints — w3_campaign, w3_ref, w3_channel, utm_* — is captured without a list edit.
 * Keys keep their original names: HQ joins on them, so nothing is lower-cased or prefix-stripped.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.trackedParamsConfig = trackedParamsConfig;
exports.isTrackedParam = isTrackedParam;
exports.pickTrackedParams = pickTrackedParams;
exports.hasTrackedParams = hasTrackedParams;
exports.hasQualifyingParams = hasQualifyingParams;
exports.touchParams = touchParams;
/** Mirror of src/config/trackedParams.ts — a safety net, not the source of truth. */
const FALLBACK_CONFIG = {
    keys: ["gclid", "fbclid", "msclkid", "agent", "promo", "utm"],
    prefixes: ["w3_", "utm_"],
    weak: ["utm", "agent", "promo"], // keep identical to src/config/trackedParams.ts
    maxValueLength: 256,
    maxParams: 25,
};
function trackedParamsConfig() {
    const injected = (typeof window !== "undefined") ? window.W3_TRACKED_PARAMS : null;
    if (injected && Array.isArray(injected.keys) && Array.isArray(injected.prefixes)) {
        return {
            keys: injected.keys.map((k) => `${k}`.toLowerCase()),
            prefixes: injected.prefixes.map((p) => `${p}`.toLowerCase()),
            weak: (Array.isArray(injected.weak) ? injected.weak : FALLBACK_CONFIG.weak).map((w) => `${w}`.toLowerCase()),
            maxValueLength: injected.maxValueLength || FALLBACK_CONFIG.maxValueLength,
            maxParams: injected.maxParams || FALLBACK_CONFIG.maxParams,
        };
    }
    return FALLBACK_CONFIG;
}
/** True if `name` is a param we capture. */
function isTrackedParam(name) {
    if (!name)
        return false;
    const cfg = trackedParamsConfig();
    const lower = `${name}`.toLowerCase();
    return cfg.keys.indexOf(lower) !== -1 || cfg.prefixes.some((p) => lower.indexOf(p) === 0);
}
/** Extract every tracked param from a URLSearchParams (or plain object). First value wins. */
function pickTrackedParams(input) {
    const out = {};
    if (!input)
        return out;
    const cfg = trackedParamsConfig();
    const maxParams = cfg.maxParams || 25;
    const maxLen = cfg.maxValueLength || 256;
    const consider = (k, v) => {
        if (Object.keys(out).length >= maxParams)
            return;
        if (out[k] !== undefined)
            return; // first value wins (matches URLSearchParams.get)
        if (!isTrackedParam(k))
            return;
        if (v === null || v === undefined)
            return;
        const str = `${v}`;
        if (str.length === 0)
            return;
        out[k] = str.slice(0, maxLen);
    };
    if (typeof URLSearchParams !== "undefined" && input instanceof URLSearchParams) {
        input.forEach((v, k) => consider(k, v));
    }
    else {
        for (const k of Object.keys(input))
            consider(k, input[k]);
    }
    return out;
}
/** True if the bag carries at least one tracked param (meta fields like `ts`/`page` don't count). */
function hasTrackedParams(bag) {
    if (!bag || typeof bag !== "object")
        return false;
    return Object.keys(bag).some((k) => isTrackedParam(k) && typeof bag[k] === "string" && bag[k].length > 0);
}
/**
 * True if the bag carries a param strong enough to record as a marketing touch: at least one that
 * is not on the weak list. `utm` is weak because the wire3.com shortcode sends it on every load of
 * a page with a campaign name configured — first-touch is write-once, so letting that stamp an
 * organic visitor would be a permanent mis-attribution.
 */
function hasQualifyingParams(bag) {
    if (!bag || typeof bag !== "object")
        return false;
    const weak = trackedParamsConfig().weak;
    return Object.keys(bag).some((k) => isTrackedParam(k)
        && weak.indexOf(k.toLowerCase()) === -1
        && typeof bag[k] === "string" && bag[k].length > 0);
}
/** The params carried by a touch, whichever shape it was written in. */
function touchParams(touch) {
    if (!touch)
        return {};
    return Object.keys(touch.params || {}).length ? pickTrackedParams(touch.params) : pickTrackedParams(touch);
}
// NOTE: the old strict-envelope / loose-formInput split (pickAttributionParams vs pickTrackingParams)
// is gone — one allow-list now feeds both, so the envelope carries exactly what marketing asked for.
